import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformSettingsContext = createContext();

export const usePlatformSettings = () => {
  const context = useContext(PlatformSettingsContext);
  if (!context) {
    throw new Error('usePlatformSettings must be used within PlatformSettingsProvider');
  }
  return context;
};

export const PlatformSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    platform_name: 'AdvanceCash.io',
    platform_tagline: 'Çalışan Avans Yönetim Platformu',
    primary_color: '#7C3AED',
    secondary_color: '#4F46E5',
    logo_url: '',
    favicon_url: '',
    custom_css: '',
    footer_text: '© 2025 AdvanceCash.io - Tüm hakları saklıdır'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlatformSettings();
  }, []);

  const loadPlatformSettings = async () => {
    try {
      const ultra_admin_token = localStorage.getItem('ultra_admin_token');
      
      if (!ultra_admin_token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/ultra-admin/platform-settings`, {
        headers: {
          'Authorization': `Bearer ${ultra_admin_token}`
        }
      });
      
      if (response.data) {
        setSettings(response.data);
        applyBrandingSettings(response.data);
      }
    } catch (error) {
      console.error('Error loading platform settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyBrandingSettings = (brandingSettings) => {
    // Update context settings state to trigger re-render of all components
    setSettings(prev => ({
      ...prev,
      primary_color: brandingSettings.primary_color || prev.primary_color,
      secondary_color: brandingSettings.secondary_color || prev.secondary_color,
      logo_url: brandingSettings.logo_url || prev.logo_url,
      favicon_url: brandingSettings.favicon_url || prev.favicon_url,
      custom_css: brandingSettings.custom_css !== undefined ? brandingSettings.custom_css : prev.custom_css,
      footer_text: brandingSettings.footer_text || prev.footer_text
    }));
    
    // Apply CSS variables for colors
    if (brandingSettings.primary_color) {
      document.documentElement.style.setProperty('--primary-color', brandingSettings.primary_color);
    }
    
    if (brandingSettings.secondary_color) {
      document.documentElement.style.setProperty('--secondary-color', brandingSettings.secondary_color);
    }

    // Apply custom CSS
    if (brandingSettings.custom_css) {
      let customStyleTag = document.getElementById('platform-custom-css');
      
      if (!customStyleTag) {
        customStyleTag = document.createElement('style');
        customStyleTag.id = 'platform-custom-css';
        document.head.appendChild(customStyleTag);
      }
      
      customStyleTag.textContent = brandingSettings.custom_css;
    }

    // Apply favicon
    if (brandingSettings.favicon_url) {
      let faviconLink = document.querySelector("link[rel*='icon']");
      
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      }
      
      faviconLink.href = brandingSettings.favicon_url;
    }

    // Update page title with platform name
    if (brandingSettings.platform_name) {
      document.title = brandingSettings.platform_name;
    }
  };

  const refreshSettings = async () => {
    await loadPlatformSettings();
  };

  return (
    <PlatformSettingsContext.Provider value={{ settings, loading, refreshSettings, applyBrandingSettings }}>
      {children}
    </PlatformSettingsContext.Provider>
  );
};
